import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './AccessDesignSolution.css';
import axios from 'axios';
import DataTable, { ActionMenu, DataTableColumn } from './DataTable';
import { Icons } from './ActionIcons';
import AppModal from './AppModal';
import AppToastContainer, { AppToastItem } from './AppToastContainer';

const URL_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

type CycleRow = {
  _id: string;
  name: string;
  profileGenClientId: string;
  clientName: string;
  clientLogo?: string;
    profileModel?: string;
  slug: string;
  status: 'draft' | 'active' | 'closed';
  createdAt?: string;
  createdBy?: { fullName?: string; username?: string } | null;
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  closed: 'Encerrado',
};
const STATUS_COLORS: Record<string, string> = {
  draft: 'secondary',
  active: 'primary',
  closed: 'success',
};

// Fetch profilegen clients for selector
type ClientOption = { clientId: string; clientName: string; profileModel: string };

const AccessDesignSolution: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CycleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState<AppToastItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<CycleRow | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [form, setForm] = useState({ name: '', profileGenClientId: '', clientName: '', slug: '', clientLogo: '', profileModel: '' });
  const [submitting, setSubmitting] = useState(false);
  const [createProgress, setCreateProgress] = useState(0);
  const createProgressTimerRef = useRef<number | null>(null);

  const clearCreateProgressTimer = () => {
    if (createProgressTimerRef.current !== null) {
      window.clearInterval(createProgressTimerRef.current);
      createProgressTimerRef.current = null;
    }
  };

  const startCreateProgress = () => {
    clearCreateProgressTimer();
    setCreateProgress(8);
    createProgressTimerRef.current = window.setInterval(() => {
      setCreateProgress((prev) => {
        if (prev >= 92) return prev;
        if (prev < 45) return prev + 7;
        if (prev < 75) return prev + 4;
        return prev + 2;
      });
    }, 300);
  };

  const addToast = (message: string, variant: AppToastItem['variant'] = 'success') => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, variant }]);
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${URL_BASE}/api/ads/cycles`, { withCredentials: true });
      setRows(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      addToast('Erro ao carregar ciclos ADS', 'danger');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${URL_BASE}/api/profilegen-clients`, { withCredentials: true });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setClients(items.map((c: any) => ({ clientId: c.clientId, clientName: c.clientName })));
      setClients(items.map((c: any) => ({ clientId: c.clientId, clientName: c.clientName, profileModel: c.profileModel || '' })));
    } catch {
      // silently ignore – clients come from profilegen module
    }
  }, []);

  useEffect(() => {
    fetchRows();
    fetchClients();
  }, [fetchRows, fetchClients]);

  useEffect(() => {
    return () => clearCreateProgressTimer();
  }, []);

  const openCreate = () => {
    setEditTarget(null);
    setCreateProgress(0);
    setForm({ name: '', profileGenClientId: '', clientName: '', slug: '', clientLogo: '', profileModel: '' });
    setShowModal(true);
  };

  const openEdit = (row: CycleRow) => {
    setEditTarget(row);
    setCreateProgress(0);
    setForm({
      name: row.name,
      profileGenClientId: row.profileGenClientId,
      clientName: row.clientName,
      slug: row.slug,
      clientLogo: String(row.clientLogo || ''),
      profileModel: row.profileModel || '',
    });
    setShowModal(true);
  };

  const handleClientSelect = (clientId: string) => {
    const client = clients.find((c) => c.clientId === clientId);
    const clientName = client?.clientName || '';
    const slug = clientName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    setForm((prev) => ({ ...prev, profileGenClientId: clientId, clientName, slug }));
    const profileModel = client?.profileModel || '';
    setForm((prev) => ({ ...prev, profileGenClientId: clientId, clientName, slug, profileModel }));
  };

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      addToast('Formato inválido. Use PNG, JPG, WEBP ou SVG.', 'warning');
      e.target.value = '';
      return;
    }
    if (file.size > 1024 * 1024) {
      addToast('Arquivo muito grande. Tamanho máximo: 1MB.', 'warning');
      e.target.value = '';
      return;
    }

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setForm((prev) => ({ ...prev, clientLogo: dataUrl }));
    } catch {
      addToast('Não foi possível ler a imagem selecionada.', 'danger');
    } finally {
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.profileGenClientId || !form.clientName.trim()) {
      addToast('Preencha todos os campos obrigatórios.', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      if (!editTarget) {
        startCreateProgress();
      }
      if (editTarget) {
        await axios.put(
          `${URL_BASE}/api/ads/cycles/${editTarget._id}`,
          { name: form.name, slug: form.slug, clientLogo: form.clientLogo || '' },
          { withCredentials: true }
        );
        addToast('Ciclo atualizado com sucesso.');
      } else {
        await axios.post(`${URL_BASE}/api/ads/cycles`, form, { withCredentials: true });
        setCreateProgress(100);
        addToast('Ciclo criado com sucesso.');
      }
      clearCreateProgressTimer();
      setShowModal(false);
      fetchRows();
    } catch (e: any) {
      clearCreateProgressTimer();
      setCreateProgress(0);
      const msg = e?.response?.data?.message || String(e);
      addToast(`Erro: ${msg}`, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await axios.delete(`${URL_BASE}/api/ads/cycles/${deleteTargetId}`, { withCredentials: true });
      addToast('Ciclo excluído.');
      setDeleteTargetId(null);
      fetchRows();
    } catch (e: any) {
      addToast(`Erro ao excluir: ${e?.response?.data?.message || String(e)}`, 'danger');
    }
  };

  const handleStatusChange = async (row: CycleRow, status: CycleRow['status']) => {
    try {
      await axios.put(`${URL_BASE}/api/ads/cycles/${row._id}`, { status }, { withCredentials: true });
      addToast(`Status alterado para "${STATUS_LABELS[status]}".`);
      fetchRows();
    } catch (e: any) {
      addToast(`Erro: ${e?.response?.data?.message || String(e)}`, 'danger');
    }
  };

  const columns: DataTableColumn<CycleRow>[] = [
    { key: 'name', label: 'Nome do Ciclo', sortable: true },
    { key: 'clientName', label: 'Cliente', sortable: true },
      { key: 'profileModel', label: 'Modelo de Perfis', sortable: true, render: (v) => v ? <span>{v}</span> : <span className="text-muted">—</span> },
    {
      key: 'status',
      label: 'Status',
      render: (v) => (
        <span className={`badge bg-${STATUS_COLORS[v] || 'secondary'}`}>{STATUS_LABELS[v] || v}</span>
      ),
    },
    { key: 'slug', label: 'Slug (URL)', render: (v) => <code className="user-select-all">{v}</code> },
    {
      key: 'createdAt',
      label: 'Criado em',
      render: (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—'),
    },
    {
      key: '_id',
      label: 'Ações',
      render: (_, row) => (
        <ActionMenu
          actions={[
            {
              key: 'open',
              label: 'Abrir ciclo',
              icon: Icons.Eye,
              onClick: () => navigate(`/ads/${row._id}`),
              variant: 'primary',
            },
            {
              key: 'activate',
              label: 'Ativar',
              icon: Icons.Check,
              onClick: () => handleStatusChange(row, 'active'),
              variant: 'success',
              disabled: row.status === 'active',
            },
            {
              key: 'close',
              label: 'Encerrar',
              icon: Icons.Lock,
              onClick: () => handleStatusChange(row, 'closed'),
              variant: 'warning',
              disabled: row.status === 'closed',
            },
            {
              key: 'edit',
              label: 'Editar',
              icon: Icons.Pencil,
              onClick: () => openEdit(row),
              variant: 'secondary',
            },
            {
              key: 'delete',
              label: 'Excluir',
              icon: Icons.Trash,
              onClick: () => setDeleteTargetId(row._id),
              variant: 'danger',
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="container py-5" style={{ marginTop: 80 }}>
      <AppToastContainer toasts={toasts} onDismiss={(id: string) => setToasts((t) => t.filter((x) => x.id !== id))} />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-0">Access Design Solution</h1>
          <p className="lead mb-0">
            Gerencie ciclos de validação de mapeamento de perfis com key users do cliente.
          </p>
        </div>
        <button className="btn btn-secondary ads-new-cycle-btn" onClick={openCreate}>
          + Novo Ciclo
        </button>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        keyExtractor={(r) => r._id}
        loading={loading}
        emptyMessage="Nenhum ciclo cadastrado."
        hover
        striped
        responsive
      />

      {/* Create / Edit modal */}
      <AppModal
          show={showModal}
          title={editTarget ? 'Editar Ciclo' : 'Novo Ciclo de Validação'}
          onClose={() => {
            if (submitting) return;
            clearCreateProgressTimer();
            setCreateProgress(0);
            setShowModal(false);
          }}
          footer={
            <>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  clearCreateProgressTimer();
                  setCreateProgress(0);
                  setShowModal(false);
                }}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Salvando…' : editTarget ? 'Salvar' : 'Criar'}
              </button>
            </>
          }
        >
          {!editTarget && submitting && (
            <div className="ads-create-progress mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Criando ciclo e clonando mapeamento para o ADS...</small>
                <small className="fw-semibold">{createProgress}%</small>
              </div>
              <div className="progress ads-create-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={createProgress}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${createProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="mb-3">
            <label className="form-label fw-semibold">Nome do Ciclo *</label>
            <input
              className="form-control form-control-sm"
              placeholder="ex.: Ciclo 1 – Redesenho 2025"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          {!editTarget && (
            <div className="mb-3">
              <label className="form-label fw-semibold">Cliente (Gerador de Perfis) *</label>
              <select
                className="form-select form-select-sm"
                value={form.profileGenClientId}
                onChange={(e) => handleClientSelect(e.target.value)}
              >
                <option value="">— selecione —</option>
                {clients.map((c) => (
                  <option key={c.clientId} value={c.clientId}>
                    {c.clientName} ({c.clientId})
                  </option>
                ))}
              </select>
            </div>
          )}
            {form.profileModel && (
              <div className="mb-3">
                <label className="form-label fw-semibold">Modelo de Perfis</label>
                <input
                  className="form-control form-control-sm bg-light"
                  value={form.profileModel}
                  readOnly
                  disabled
                />
                <small className="text-muted">Herdado do Gerador de Perfis. Influencia a revisão no ADS.</small>
              </div>
            )}
            <div className="mb-3">
            <label className="form-label fw-semibold">Slug da URL</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text text-muted" style={{ fontSize: '0.78rem' }}>
                /ADS/
              </span>
              <input
                className="form-control"
                placeholder="nome-do-cliente"
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              />
            </div>
            <small className="text-muted">URL de acesso do key user ao portal.</small>
          </div>

          <div className="mb-1">
            <label className="form-label fw-semibold">Logo do cliente (opcional)</label>
            <div className="d-flex align-items-start gap-3 flex-wrap">
              <div className="ads-logo-preview-box">
                {form.clientLogo ? (
                  <img src={form.clientLogo} alt="Logo do cliente" className="ads-logo-preview" />
                ) : (
                  <span className="ads-logo-preview-empty">Sem logo</span>
                )}
              </div>
              <div className="d-flex flex-column gap-2">
                <input
                  className="form-control form-control-sm"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  onChange={handleLogoFileChange}
                />
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setForm((p) => ({ ...p, clientLogo: '' }))}
                    disabled={!form.clientLogo}
                  >
                    Remover logo
                  </button>
                </div>
                <small className="text-muted">Dimensão livre, exibição proporcional no portal ADS (sem distorção).</small>
              </div>
            </div>
          </div>
        </AppModal>

      {/* Confirm delete */}
      <AppModal
          show={!!deleteTargetId}
          title="Confirmar exclusão"
          onClose={() => setDeleteTargetId(null)}
          footer={
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTargetId(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                Excluir
              </button>
            </>
          }
        >
          <p>Tem certeza que deseja excluir este ciclo? Todos os key users associados também serão removidos.</p>
        </AppModal>
    </div>
  );
};

export default AccessDesignSolution;
